
 // components/groupChatHistory.js
//
// Fetches encrypted group history in pages.
// The caller saves these worker-ready records to IndexedDB.
// Messages are returned oldest → newest.
//
// Keep the existing IndexedDB-first bootstrap behavior:
// call this for initial seeding only when required.

import axios from "axios";

const PAGE_SIZE = 100;
const MAX_PAGES_PER_REQUEST = 10000;

/* =========================================================
   fetchGroupChatHistory
   Loads all available group history in batches.
   Returns raw server messages (oldest → newest).
========================================================= */
export const fetchGroupChatHistory = async (
  groupId,
  myChatId,
  socket = null // kept for call-site compatibility
) => {
  if (!groupId || !myChatId) return [];

  const allMessages = [];
  const seenIds = new Set();
  let before = null;
  let pageCount = 0;

  try {
    while (pageCount < MAX_PAGES_PER_REQUEST) {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
      });

      if (before) {
        params.set("before", before);
      }

      const res = await axios.get(
        `/api/groups/${encodeURIComponent(groupId)}/${encodeURIComponent(myChatId)}?${params.toString()}`,
        { withCredentials: true }
      );

      const page = res.data?.messages;

      if (!Array.isArray(page) || page.length === 0) {
        break;
      }

      for (const message of page) {
        const id =
          message?.id ||
          message?.msgId ||
          message?.messageId;

        if (!id) {
          allMessages.push(message);
        } else if (!seenIds.has(String(id))) {
          seenIds.add(String(id));
          allMessages.push(message);
        }
      }

      pageCount += 1;

      if (res.data?.hasMore !== true || !res.data?.nextCursor) {
        break;
      }

      const nextBefore = String(res.data.nextCursor);

      // Prevent an endless loop if the server repeats a cursor.
      if (nextBefore === before) {
        break;
      }

      before = nextBefore;
    }

    allMessages.sort((a, b) => {
      const timeA = Number(a?.timestamp) || 0;
      const timeB = Number(b?.timestamp) || 0;

      if (timeA !== timeB) {
        return timeA - timeB;
      }

      return String(a?.id || a?.msgId || "").localeCompare(
        String(b?.id || b?.msgId || "")
      );
    });

    return allMessages;
  } catch {
    // Do not report a partially fetched history as complete.
    return [];
  }
};

/* =========================================================
   fetchMoreGroupChatHistory
   Loads one older page for scroll-back pagination.

   The socket parameter is retained to avoid breaking
   existing call sites.
========================================================= */
export const fetchMoreGroupChatHistory = async (
  groupId,
  myChatId,
  socket = null,
  beforeMsgId,
  limit = 50
) => {
  if (!groupId || !myChatId || !beforeMsgId) {
    return [];
  }

  try {
    const safeLimit = Math.max(
      1,
      Math.min(Number(limit) || 50, PAGE_SIZE)
    );

    const params = new URLSearchParams({
      before: String(beforeMsgId),
      limit: String(safeLimit),
    });

    const res = await axios.get(
      `/api/groups/${encodeURIComponent(groupId)}/${encodeURIComponent(myChatId)}?${params.toString()}`,
      { withCredentials: true }
    );

    const messages = res.data?.messages;

    return Array.isArray(messages) ? messages : [];
  } catch {
    return [];
  }
};
